<script setup lang="ts">
import { useStripeCheckout } from '@/use/stripeCheckout';
import { useI18n } from '@/i18n';
import { usePaymentStore } from '@/stores/payment';
import { Alert, LayoutGap } from '@dabble/quillui';
import { onMounted, ref } from 'vue';

const props = defineProps<{
  getReturnUrl: () => string;
  onConfirmed: () => Promise<void>;
  onAddressSaved?: () => void | Promise<void>;
}>();

const { t } = useI18n();
const paymentStore = usePaymentStore();

const paymentContainer = ref<HTMLDivElement | null>(null);
const expressContainer = ref<HTMLDivElement | null>(null);
const addressContainer = ref<HTMLDivElement | null>(null);

const {
  isInitializing,
  isPaymentComplete,
  isSubmitting,
  isAddressCollapsed,
  billingAddressSummary,
  revealAddress,
  init,
  submit,
} = useStripeCheckout({
  paymentContainer,
  expressContainer,
  addressContainer,
  getReturnUrl: props.getReturnUrl,
  onConfirmed: props.onConfirmed,
  onAddressSaved: props.onAddressSaved,
});

onMounted(() => {
  void init();
});

defineExpose({ isInitializing, isPaymentComplete, isSubmitting, submit });
</script>

<template>
  <LayoutGap direction="column" size="small" fill class="billing-stripe-payment-form">
    <span class="billing-stripe-payment-form__section-label">{{ t('checkout_payment_method') }}</span>

    <div role="alert" aria-live="assertive" aria-atomic="true">
      <!-- Body goes in the default slot: Alert has no `message` prop, so passing one
           silently rendered a bare title with no reason (DAB-957). -->
      <Alert v-if="paymentStore.error" variant="danger" :title="t('billing_payment_error')">
        {{ paymentStore.error }}
      </Alert>
    </div>
    <div v-if="isInitializing" class="billing-stripe-payment-form__loading" aria-busy="true"></div>
    <div ref="expressContainer" class="billing-stripe-payment-form__express"></div>
    <div v-if="!isInitializing" class="billing-stripe-payment-form__divider">
      <span>{{ t('checkout_or_pay_using') }}</span>
    </div>
    <div ref="paymentContainer" class="billing-stripe-payment-form__stripe"></div>
    <span
      v-if="!isInitializing"
      class="billing-stripe-payment-form__section-label billing-stripe-payment-form__section-label--spaced"
    >
      {{ t('checkout_billing_address') }}
    </span>
    <div v-if="!isInitializing && isAddressCollapsed" class="billing-stripe-payment-form__address-summary">
      <span class="billing-stripe-payment-form__address-text">{{ billingAddressSummary }}</span>
      <button type="button" class="billing-stripe-payment-form__address-change" @click="revealAddress">
        {{ t('checkout_billing_address_change') }}
      </button>
    </div>
    <div ref="addressContainer" class="billing-stripe-payment-form__address"></div>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.billing-stripe-payment-form {
  flex: 1 1 20rem;
  min-width: 0;

  &__stripe {
    width: 100%;
    min-height: 144px;
  }

  // Consistent section label, matching ORDER SUMMARY on the checkout summary card.
  &__section-label {
    display: block;
    font-size: fontSize('xsmall');
    font-weight: fontWeight('semibold');
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: theme('color-text-placeholder');
  }

  // Billing Address isn't the first section, so give it extra separation from the
  // payment content above (on top of the container's small gap).
  &__section-label--spaced {
    margin-top: space('medium');
  }

  &__address-summary {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: space('small');
    flex-wrap: wrap;
  }

  &__address-text {
    font-size: 1rem;
    color: theme('color-text-primary');
  }

  &__address-change {
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    font: inherit;
    font-size: fontSize('small');
    font-weight: fontWeight('medium');
    color: theme('color-bg-primary-interactive');
    text-decoration: underline;
  }

  &__address {
    width: 100%;

    &:empty {
      display: none;
    }
  }

  &__express {
    width: 100%;

    &:empty {
      display: none;
    }
  }

  &__divider {
    display: flex;
    align-items: center;
    gap: space('small');
    width: 100%;
    margin: space('medium') 0;
    color: theme('color-text-placeholder');
    font-size: fontSize('small');

    &::before,
    &::after {
      content: '';
      flex: 1;
      border-top: 1px solid var(--border-color, #e5e7eb);
    }
  }

  // No wallets available → express renders empty; drop the lone divider too.
  &__express:empty + &__divider {
    display: none;
  }

  &__loading {
    min-height: 144px;
    width: 100%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 8px;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
